import { FeRecordType } from "config/config";

/**
 * Type definitions for the configurable dashboard `value` config.
 *
 * A DASHBOARD resource stores one {@link DashboardConfig} in its `value` column.
 * The dashboard viewer ({@link ../DashboardView}) renders it as a config-driven
 * CSS grid of interactive widgets. The shape is intentionally permissive: unknown
 * widget types and partial widget configs render a fallback rather than crashing,
 * so older viewers can open newer configs.
 */

/** Placement of a widget within the dashboard grid. 1-based, like CSS grid lines. */
export interface WidgetLayout {
    /** 1-based start column. Defaults to auto-flow when omitted. */
    col?: number;
    /** 1-based start row. Defaults to auto-flow when omitted. */
    row?: number;
    /** Number of columns to span. Defaults to 1. */
    colSpan?: number;
    /** Number of rows to span. Defaults to 1. */
    rowSpan?: number;
}

export interface DashboardGrid {
    /** Number of columns in the grid. Defaults to 12. */
    columns?: number;
    /** Gap between cells, in pixels. Defaults to 16. */
    gap?: number;
    /** Height of a single grid row, in pixels. Defaults to 80. */
    rowHeight?: number;
}

/** A `markdown` widget: renders static markdown (notes, runbook links, …). */
export interface MarkdownWidgetConfig {
    text?: string;
}

/** A `record_list` widget: a live list of records of a chosen type, optionally filtered. */
export interface RecordListWidgetConfig {
    /** Record type to list, e.g. "CUSTOM_OBJECT", "FLOW". */
    record_type: FeRecordType;
    /** Optional filter, using the existing filter JSON grammar (serialized to the GraphQL `filter` arg). */
    filter?: unknown;
    /** Order string, e.g. "-created_at". */
    order?: string;
    /**
     * Maximum number of records to show, with fixed-count pagination.
     *
     * When UNSET the widget is height-adaptive: it sizes the number of rows to
     * the space available in the widget (measured from the rendered table) and
     * shows them on a single, footer-less page — so a taller widget shows more
     * rows and pagination is skipped. Set an explicit number to opt out and get
     * a fixed row count with pagination.
     */
    limit?: number;
    /**
     * CUSTOM_OBJECT display hint (item 2): when set and `record_type` is
     * CUSTOM_OBJECT, the table ALSO renders this object template's attributes
     * (the CO `value.<attr>` fields) as columns — the same attribute columns the
     * object_template resource screen shows for its custom objects.
     *
     * This is a pure DISPLAY hint, DECOUPLED from `filter`: it does not restrict
     * which rows load. If `filter` selects custom objects of a different template,
     * those rows simply show blank attribute cells. (Stefan flagged the potential
     * inconsistency — see operator-question on this task; the decoupled semantics
     * keep "which rows" and "which attribute columns" independent knobs.)
     */
    object_template_id?: string;
    /**
     * Explicit ordered list of column keys to show by default (item: configurable
     * default columns). When set, exactly these columns are shown, in this order;
     * every other column stays available in the column selector but hidden by
     * default. When UNSET, the automatic per-record_type default column set is
     * used unchanged.
     *
     * Keys are the RecordTable column keys: record fields ("name", "location",
     * "modified_at", "modified_by", "created_at", "created_by"), the CUSTOM_OBJECT
     * "object_template" column, and — when `object_template_id` is set — that
     * template's attribute names (`value.<attr>` columns are keyed by the bare
     * attribute name, e.g. "status", "priority").
     */
    columns?: string[];
    /**
     * When true, the list refreshes on a short interval so provisioning state stays current.
     * (v1: polling; a websocket subscription can replace it later.)
     */
    live?: boolean;
    /**
     * Row density of the list: "medium" (Comfortable), "high" (Standard, the
     * default) or "compact" (one line per row). A user's pick in the table
     * toolbar overrides it for that widget.
     */
    density?: "medium" | "high" | "compact";
}

/**
 * A `single_record` widget: shows ONE record (first match of `filter`, ordered by
 * `order`). Re-uses the `record_list` config shape (record_type + filter + order +
 * live). By default it renders a compact detail card (name link + description +
 * modified-at). For a CUSTOM_OBJECT you may instead surface a computed field of the
 * record's JSON `value` as the tile body via {@link markdown_field} — e.g. a
 * pre-rendered markdown summary the record already carries.
 */
export interface SingleRecordWidgetConfig extends RecordListWidgetConfig {
    /**
     * CUSTOM_OBJECT only: render `value.<markdown_field>` as markdown as the tile
     * body (instead of the default name/description card). Lets a dashboard tile
     * surface a record's own computed markdown field — e.g. the throttle-log CO's
     * weekly `summary`. Ignored for non-CUSTOM_OBJECT record types or when the
     * field is absent/empty (falls back to the default card / an empty state).
     */
    markdown_field?: string;
}

/** Partition spec for `latest_n_list`: last N records per distinct field value. */
export interface PartitionByWidgetConfig {
    /** Field path to partition on, e.g. "project_id", "type", "status". */
    field: string;
    /** Max rows per distinct field value. Config `n` is shorthand. Defaults to 1. */
    limit?: number;
    /** Ordering within each partition. Falls back to the widget-level `order`. */
    order?: string;
}

/** A `relation_graph` widget: embedded object-template or custom-object relation canvas. */
export interface RelationGraphWidgetConfig {
    mode: "object_template" | "custom_object";
    /** Filter for object_template_list or custom_object_list (standard filter JSON grammar). */
    filter?: unknown;
    /**
     * For `custom_object` mode: optional alias for `filter` when seeding from custom objects.
     * Prefer `filter` for new configs.
     */
    custom_object_filter?: unknown;
    /**
     * For `custom_object` mode: pick the last N custom objects per distinct field value
     * (e.g. latest installation per object template type). Uses `partition_by` on the list API.
     */
    partition_by?: PartitionByWidgetConfig;
    /** Shorthand for `partition_by.limit`. Defaults to 1. */
    n?: number;
    /** Max number of groups when using `partition_by`. Defaults to 50. */
    limit?: number;
    /** Default sort when `partition_by.order` is omitted. Defaults to `-created_at`. */
    order?: string;
}

/** A `latest_n_list` widget: last N records per group via `partition_by`. */
export interface LatestNListWidgetConfig {
    /** Record type to list, e.g. "EXECUTION", "FLOW". */
    record_type: FeRecordType;
    /** Required partition spec — returns top N rows per distinct `field` value. */
    partition_by: PartitionByWidgetConfig;
    /** Shorthand for `partition_by.limit`. */
    n?: number;
    /** Optional filter, using the existing filter JSON grammar. */
    filter?: unknown;
    /** Max number of groups (partitions). Defaults to 20. */
    limit?: number;
    /**
     * Sort order for partition groups in the widget (e.g. `name`, `-created_at`).
     * Within-partition order uses `partition_by.order`, falling back to this value.
     * Defaults to "-created_at".
     */
    order?: string;
    /** When true, re-poll on a short interval. */
    live?: boolean;
}

/** One entry of a `status_tile` value map: the tile appearance for a field value. */
export interface StatusTileValueMapEntry {
    /**
     * StatusBadge colour-token name for the indicator dot, e.g. "primary-green",
     * "primary-yellow", "dark-grey", "primary-red". Uses the named palette tokens
     * (no ad-hoc hex) — see the color-palette design principle.
     */
    color: string;
    /** Human-readable label shown next to the indicator. */
    label: string;
    /** Optional static sub-line shown under the label. */
    detail?: string;
    /**
     * Optional: read this field off the record and render it as the sub-line
     * (overrides `detail`). For CUSTOM_OBJECT it reads `value.<detail_field>`.
     * Useful for e.g. showing the approximate resume time on a paused state.
     */
    detail_field?: string;
    /** How to format `detail_field`. "datetime" → localized time + " (approx)". */
    detail_field_format?: "datetime" | "text";
}

/** Optional staleness/heartbeat override for `status_tile` (the "operator is dead" case). */
export interface StatusTileStaleConfig {
    /**
     * Field holding an ISO timestamp the record is considered "fresh until"
     * (e.g. "sleeping_until"). For CUSTOM_OBJECT it reads `value.<timestamp_field>`.
     */
    timestamp_field: string;
    /** Grace seconds added to the timestamp before the record counts as stale. */
    grace_seconds?: number;
    /** Field values for which staleness must NOT trigger (states that sleep indefinitely, e.g. "paused"). */
    skip_when_values?: string[];
    color: string;
    label: string;
    detail?: string;
}

/**
 * A `status_tile` widget: a single traffic-light-style tile that reads ONE field
 * off ONE record (first match of `filter`) and maps its value to a coloured
 * indicator + label via `value_map`. For CUSTOM_OBJECT records the field is read
 * from the JSON `value` (`value.<field>`); for other types it is a top-level
 * column. An optional `stale` block turns a missing heartbeat into a dead state.
 */
export interface StatusTileWidgetConfig {
    /** Record type to read, e.g. "CUSTOM_OBJECT". */
    record_type: FeRecordType;
    /** Optional filter (first match, like `single_record`) selecting the one record. */
    filter?: unknown;
    /** Order string, e.g. "-modified_at". */
    order?: string;
    /** Field whose value selects the tile state (read from `value.<field>` for CUSTOM_OBJECT). */
    field: string;
    /** Map of field value → tile appearance. */
    value_map: Record<string, StatusTileValueMapEntry>;
    /** Fallback appearance when the value matches no `value_map` key. */
    default?: StatusTileValueMapEntry;
    /** Optional staleness override → dead/no-heartbeat state. */
    stale?: StatusTileStaleConfig;
    /** When true, re-poll on a short interval. */
    live?: boolean;
}

/**
 * A `status_matrix` widget: grouped rows of coloured status cells, each cell
 * optionally linking to a record or URL.
 *
 * Reads a pre-aggregated matrix off a SETTING record's JSON `value` (whose name
 * the user configures; the user's own flow writes it), so the widget makes ONE
 * cheap query instead of many live lookups. Value shape: see `MatrixValue` in
 * widgets/statusMatrixState.ts.
 */
export interface StatusMatrixWidgetConfig {
    /** Name of the SETTING whose JSON `value` holds the aggregated matrix. Required — there is no default. */
    setting_name?: string;
    /**
     * Optional subset/ordering of matrix groups to show, by group `label`. When
     * unset, all groups present in the setting are shown in their stored order.
     */
    groups?: string[];
    /** Former name of `groups`; read only when `groups` is unset. */
    modes?: string[];
    /** When true, re-poll on a short interval so the matrix stays current. */
    live?: boolean;
}

export type WidgetConfig =
    | MarkdownWidgetConfig
    | RecordListWidgetConfig
    | SingleRecordWidgetConfig
    | LatestNListWidgetConfig
    | RelationGraphWidgetConfig
    | StatusTileWidgetConfig
    | StatusMatrixWidgetConfig
    | Record<string, unknown>;

export interface DashboardWidget {
    /** Stable unique id of the widget within the dashboard (used as React key). */
    uid: string;
    /** Widget type; looked up in the widget registry. */
    type: string;
    /** Optional heading shown in the widget frame. */
    title?: string;
    layout?: WidgetLayout;
    config?: WidgetConfig;
}

export interface DashboardConfig {
    version?: number;
    grid?: DashboardGrid;
    widgets?: DashboardWidget[];
}

export const DEFAULT_GRID_COLUMNS = 12;
export const DEFAULT_GRID_GAP = 16;
export const DEFAULT_GRID_ROW_HEIGHT = 80;

/** Props every widget component receives from the registry. */
export interface WidgetComponentProps<TConfig = WidgetConfig> {
    widget: DashboardWidget;
    config: TConfig;
}
